import {MoreHoriz} from '@mui/icons-material';
import {Box} from '@mui/material';
import {createFileRoute, useNavigate} from '@tanstack/react-router';
import {useState} from 'react';
import {useTranslation} from 'tolgee';

import {theme} from '~constants/theme';
import {useFeatureFlags} from '~utils/featureFlags';
import {getRoute, Route as RouteEnum} from '~utils/routeUtil';
import {useTrackEvent} from '~hooks/analytics.hooks';
import {InspectionType} from '~redux/types/models';

import {ConfirmDialog} from '~components/common/ConfirmDialog';
import {IconMenuItem} from '~components/common/menu-items/IconMenuItem';
import {ToolbarMenu} from '~components/common/toolbar/ToolbarMenu';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {CloneModelDialog} from '~components/models/clone/CloneModelDialog';
import {ModelSamples} from '~components/models/samples/ModelSamples';
import {useArchiveModelMutation, useCloneModelMutation} from 'src/api/models.mutations';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';
import {CopyIcon, ModelArchiveIcon, SettingsIcon, StatisticsIcon} from 'src/icons';

export const Route = createFileRoute('/model/samples/')({
  component: ModelSamplesPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      projectID: (search.projectID as string) || undefined,
      modelID: (search.modelID as string) || undefined,
      inspection_type: (search.inspection_type as InspectionType) || undefined,
      imagesOverviewViewMode: (search.imagesOverviewViewMode as string) || undefined,
    };
  },
});

function ModelSamplesPage(): JSX.Element {
  return (
    <ModelProvider mode='model-pages'>
      <ModelSamplesPageContent />
    </ModelProvider>
  );
}

function ModelSamplesPageContent(): JSX.Element {
  const {t} = useTranslation(['models', 'common']);
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const {currentModel} = useCurrentModel();
  const isArchived = currentModel?.archived;

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const archiveModelMutation = useArchiveModelMutation();
  const cloneModelMutation = useCloneModelMutation();

  const {toggleEnableInsights} = useFeatureFlags();

  const handleCloneModelClick = () => {
    trackEvent('model_samples_clone_model_click', {modelId: currentModel?.id});
    setIsCloneDialogOpen(true);
  };

  return (
    <>
      <BaseLayout
        customContentAndFooter
        showBackButton
        titleActions={
          <Box
            sx={{
              ml: theme.spacing(0.5),
              '.MuiIconButton-root': {
                padding: 0,
                margin: 0,
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                  color: theme.palette.maddox.globalblack,
                },
              },
            }}
          >
            <ToolbarMenu id='more-options-menu' icon={<MoreHoriz />}>
              {toggleEnableInsights && (
                <IconMenuItem
                  text={t('statistics', {ns: 'common'})}
                  icon={<StatisticsIcon />}
                  onClick={() => navigate({to: getRoute(RouteEnum.modelLabelDistribution) as any})}
                />
              )}
              <IconMenuItem
                text={t('modelSettings', {ns: 'models'})}
                icon={<SettingsIcon />}
                onClick={() => navigate({to: getRoute(RouteEnum.modelSettings) as any})}
              />
              <IconMenuItem
                text={t('cloneModel.title', {ns: 'models'})}
                icon={<CopyIcon />}
                disabled={!currentModel || cloneModelMutation.isPending}
                onClick={handleCloneModelClick}
              />
              <IconMenuItem
                text={t('archiveModel.title', {ns: 'models'})}
                icon={<ModelArchiveIcon />}
                disabled={isArchived}
                onClick={() => setIsArchiveDialogOpen(true)}
              />
            </ToolbarMenu>
          </Box>
        }
      >
        <ModelSamples />
      </BaseLayout>
      <ConfirmDialog
        maxWidth='xs'
        fullWidth
        open={isArchiveDialogOpen}
        onClose={() => setIsArchiveDialogOpen(false)}
        onConfirm={() => {
          archiveModelMutation.mutate(
            {modelId: currentModel!.id},
            {
              onSuccess: () => {
                trackEvent('model_archived', {modelId: currentModel?.id});
                navigate({to: getRoute(RouteEnum.models) as any});
              },
            },
          );
        }}
        onDiscard={() => setIsArchiveDialogOpen(false)}
        title={t('archiveModel.areYouSure')}
        description={
          <>
            {t('archiveModel.description')}
            <br />
            <br />
            {t('archiveModel.canBeRestored')}
          </>
        }
        confirmText={t('archiveModel.confirm')}
      />
      {currentModel && (
        <CloneModelDialog
          model={currentModel}
          open={isCloneDialogOpen}
          onClose={() => setIsCloneDialogOpen(false)}
          isSubmitting={cloneModelMutation.isPending}
          onClone={(payload) =>
            cloneModelMutation.mutate(
              {modelId: currentModel.id, payload},
              {
                onSuccess: () => {
                  setIsCloneDialogOpen(false);
                  navigate({to: getRoute(RouteEnum.models) as any});
                },
              },
            )
          }
        />
      )}
    </>
  );
}
